import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import StatCard from "@/components/StatCard";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { listCases } from "@/lib/offlineApi";
import {
  Activity, CalendarDays, Percent,
  PlusCircle, FileText, BarChart3, Download, ArrowRight, Target,
} from "lucide-react";

function QuickAction({ to, icon: Icon, title, subtitle, testId }) {
  return (
    <Link
      to={to}
      className="neu-card p-6 group hover:shadow-neu-inset transition-shadow duration-300"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-neu-bg flex items-center justify-center shadow-neu-inset-sm">
          <Icon className="w-5 h-5 text-neu-accent" />
        </div>
        <ArrowRight className="w-4 h-4 text-neu-muted group-hover:text-neu-accent group-hover:translate-x-1 transition-transform" />
      </div>
      <div className="heading text-lg text-[#3F4E5B]">{title}</div>
      <div className="text-xs subtle mt-1">{subtitle}</div>
    </Link>
  );
}

function BoardProgress({ current, target, pct }) {
  return (
    <div className="neu-card p-6" data-testid="board-progress">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-neu-accent" />
          <div className="heading text-lg text-[#3F4E5B]">Board progress</div>
        </div>
        <div className="text-sm subtle">
          <span className="font-semibold text-neu-text">{current}</span> / {target} cases
        </div>
      </div>
      <div className="h-4 rounded-full bg-neu-bg shadow-neu-inset-sm overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-neu-accent to-[#AED581] transition-all"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="text-xs subtle mt-2">{pct}% of target</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, cases] = await Promise.all([
          api.get("/analytics").then((r) => r.data).catch(() => null),
          listCases().catch(() => []),
        ]);
        setAnalytics(a);
        setRecent((cases || []).slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = analytics || {};
  return (
    <Layout>
      <div className="mb-8" data-testid="dashboard-welcome">
        <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">
          Welcome back, {user?.name?.split(" ")[0] || "Fellow"}.
        </h1>
        <p className="subtle mt-2">
          {user?.specialty || "Surgical fellowship"}
          {user?.fellowship_program ? ` · ${user.fellowship_program}` : ""}
          {user?.fellowship_year ? ` · Year ${user.fellowship_year}` : ""}
        </p>
      </div>

      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="neu-card p-6 h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <StatCard
            testId="stat-total"
            icon={Activity}
            label="Total cases"
            value={stats.total_cases ?? 0}
            sub="All time"
            accent
          />
          <StatCard
            testId="stat-month"
            icon={CalendarDays}
            label="This month"
            value={stats.cases_this_month ?? 0}
            sub="Cases logged"
          />
          <StatCard
            testId="stat-primary"
            icon={Percent}
            label="Primary surgeon"
            value={`${stats.primary_surgeon_pct ?? 0}%`}
            sub="Cases as primary"
          />
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mt-8">
        <QuickAction to="/cases/new" icon={PlusCircle} title="Log new case" subtitle="Add a case in seconds" testId="quick-new-case" />
        <QuickAction to="/cases" icon={FileText} title="View case log" subtitle="Search & filter cases" testId="quick-cases" />
        <QuickAction to="/analytics" icon={BarChart3} title="Analytics" subtitle="Trends & breakdowns" testId="quick-analytics" />
        <QuickAction to="/cases" icon={Download} title="Export log" subtitle="Excel or PDF" testId="quick-export" />
      </div>

      {stats.board && (
        <div className="mt-8">
          <BoardProgress
            current={stats.board.current}
            target={stats.board.target}
            pct={stats.board.progress_pct}
          />
        </div>
      )}

      <div className="mt-8 neu-card p-6" data-testid="recent-cases">
        <div className="flex items-center justify-between mb-4">
          <div className="heading text-xl text-[#3F4E5B]">Recent cases</div>
          <Link to="/cases" className="text-sm text-neu-accent hover:underline">
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-8">
            <div className="subtle mb-4">No cases yet — log your first case</div>
            <Link to="/cases/new" className="btn-accent inline-flex">
              <PlusCircle className="w-4 h-4 mr-2" /> Log a case
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left subtle text-xs uppercase tracking-wider border-b border-neu-muted/20">
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Procedure</th>
                  <th className="py-3 pr-4">Attending</th>
                  <th className="py-3 pr-4">Role</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-b border-neu-muted/10 last:border-b-0">
                    <td className="py-3 pr-4 whitespace-nowrap">{c.date_of_procedure}</td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-neu-text line-clamp-1">{c.procedure_name}</div>
                      <div className="text-xs subtle">{c.procedure_category}</div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{c.attending_physician}</td>
                    <td className="py-3 pr-4">
                      <span className={`chip ${c.role_in_case === "Primary Surgeon" ? "chip-green" : ""}`}>
                        {c.role_in_case}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
